import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import { t } from 'i18next';
import * as yup from 'yup';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm, useFormContext, useWatch } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';

import Button from 'components/field/Button';
import ProgressWizard from 'components/field/ProgressWizard';
import StringInput from 'components/field/StringInput';
import SwitchInput from 'components/field/SwitchInput';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import Alert from 'components/global/Alert';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';

import { WHATSAPP } from 'services/consts';

import AppLogger from 'utils/AppLogger';

import { faAngleLeft, faAngleRight, faBookOpen, faKey, faPlug, faWrench } from '@fortawesome/pro-solid-svg-icons';

import WhatsappBusinessInstructions from './WhatsappBusinessInstructions';
import * as S from './WhatsappBusinessModal.styled';
import {
	setDefaultConnection,
	setMode as setFormMode,
	setIsLoading,
	setIsVerified,
	setLastVerifiedValues,
	setStateToDefault
} from './whatsappBusinessSlice';

const Instructions = () => {
	const isLoading = useSelector((state) => state.whatsappBusiness.isLoading);
	return (
		<S.BusinessInstructionWrapper>
			<WhatsappBusinessInstructions isLoading={isLoading} />
		</S.BusinessInstructionWrapper>
	);
};

const Credentials = () => {
	const isLoading = useSelector((state) => state.whatsappBusiness.isLoading);
	const isVerified = useSelector((state) => state.whatsappBusiness.isVerified);
	const mode = useSelector((state) => state.whatsappBusiness.mode);
	const dispatch = useDispatch();

	const axios = useAxiosPrivate();
	const { showError, showSuccess } = useAlert();
	const [connectionTesting, setConnectionTesting] = useState(false);

	const formMethods = useFormContext();
	const { control, trigger, setValue } = formMethods;
	const defaultColSpan = {
		md: 6
	};

	const errors = useMemo(() => ['error.WA001', 'error.WB001'], []);

	const [businessId, userAccessToken] = useWatch({
		control,
		name: ['businessId', 'userAccessToken']
	});

	const testConnection = useCallback(async () => {
		const validID = await trigger(['businessId', 'userAccessToken'], { shouldFocus: false });
		if (!validID) {
			showError({
				message: 'errorMsg.enterWhatsappCredentials'
			});
		} else {
			dispatch(setIsLoading(true));
			setConnectionTesting(true);
			await axios
				.post(WHATSAPP.TEST_WHATSAPP_CONNECTION, {
					businessId,
					userAccessToken
				})
				.then((res) => {
					const { data } = res;
					if (data?.error) {
						dispatch(setIsVerified(false));
						showError({
							title: 'errorMsg.whatsappConnectionTest',
							message: t(data?.error?.code)
						});
					} else {
						dispatch(setIsVerified(true));
						if (data?.name) {
							setValue('connectionName', data?.name, {
								shouldValidate: true,
								shouldDirty: true
							});
						}
						showSuccess({
							message: 'success.whatsappConnectionVerified'
						});
					}
				})
				.catch((error) => {
					if (errors.includes(error?.response?.data?.code)) {
						showError({
							message: error?.response?.data?.code
						});
					} else {
						showError({
							message: 'errorMsg.whatsappConnectionTest'
						});
						AppLogger.error(`Error while testing whatsapp connection: ${JSON.stringify(error)}`);
					}
				});
			setConnectionTesting(false);
			dispatch(setIsLoading(false));
		}
	}, [businessId, userAccessToken]);

	useEffect(() => {
		if (isVerified) {
			dispatch(
				setLastVerifiedValues({
					businessId,
					userAccessToken
				})
			);
		}
	}, [isVerified]);

	return (
		<>
			<S.Credentials>
				<S.CredentialFormItems>
					<FormItem colSpan={defaultColSpan} label="whatsapp.businessId" isLoading={isLoading} required>
						<StringInput name="businessId" disabled={mode !== 'add'} />
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="whatsapp.userAccessToken" isLoading={isLoading} required>
						<StringInput name="userAccessToken" disabled={mode !== 'add'} />
					</FormItem>
				</S.CredentialFormItems>
				{!isVerified && (
					<S.VerifyAlert>
						<Alert content="whatsapp.verifyYourConnection" variant="warning" showIcon />
					</S.VerifyAlert>
				)}
				{mode === 'add' && (
					<S.CredentialButtonContainer>
						<Button
							label="whatsapp.verifyConnection"
							variant="black"
							disabled={connectionTesting}
							icon={faPlug}
							onClick={testConnection}
						/>
					</S.CredentialButtonContainer>
				)}
			</S.Credentials>
		</>
	);
};

const Settings = () => {
	const mode = useSelector((state) => state.whatsappBusiness.mode);
	const isLoading = useSelector((state) => state.whatsappBusiness.isLoading);

	const defaultConnection = useSelector((state) => state.whatsappBusiness.defaultConnection);

	const defaultColSpan = {
		md: 6
	};
	// const webhookUrlWatcher = useWatch({
	// 	control,
	// 	name: 'webhookUrl'
	// });

	return (
		<>
			<FormItem colSpan={defaultColSpan} label="connectionName" isLoading={isLoading} required>
				<StringInput name="connectionName" />
			</FormItem>
			<FormItem colSpan={defaultColSpan} label="senderName" isLoading={isLoading} required>
				<StringInput name="senderName" />
			</FormItem>
			<FormItem colSpan={defaultColSpan} label="whatsapp.phoneNumberId" isLoading={isLoading} required>
				<StringInput name="phoneNumberId" disabled={mode !== 'add'} />
			</FormItem>
			{/* <FormItem colSpan={defaultColSpan} label="webhookUrl" isLoading={isLoading} toolTip="tooltip.webhookUrl">
				<CopyComponent value={webhookUrlWatcher} buttonDisabled={webhookUrlWatcher ? false : true} />
			</FormItem> */}
			<FormItem
				colSpan={defaultColSpan}
				label="active"
				direction="column"
				isLoading={isLoading}
				childStyle={{ marginTop: '1.25rem' }}>
				<SwitchInput name="isActive" />
			</FormItem>
			<FormItem
				colSpan={defaultColSpan}
				label="whatsapp.defaultConnection"
				direction="column"
				isLoading={isLoading}
				childStyle={{ marginTop: '1.25rem' }}
				toolTip="tooltip.defaultWpConnection">
				<SwitchInput name="isDefault" disabled={defaultConnection} />
			</FormItem>
			<FormItem
				colSpan={defaultColSpan}
				label="importExistingTemplates"
				direction="column"
				isLoading={isLoading}
				childStyle={{ marginTop: '1.25rem' }}
				toolTip="tooltip.importExistingTemplates">
				<SwitchInput name="importExistingTemplates" />
			</FormItem>
		</>
	);
};

const ProgressWizardTabs = ({ tab }) => {
	const progressWizardTabs = (tab) => {
		switch (tab) {
			case 0:
				return <Instructions />;
			case 1:
				return <Credentials />;
			case 2:
				return <Settings />;
			default:
				return null;
		}
	};

	return progressWizardTabs(tab);
};

const WhatsappBusinessModal = NiceModal.create(
	({
		logo = 'https://storage.googleapis.com/tryoto-public/other-integrations-logo/whatsapp-business.png',
		smsConfigID,
		mode = 'add',
		setConnection = null
	}) => {
		const modal = useModal();
		const axiosPrivate = useAxiosPrivate();
		const { showError, showWarning, showSuccess } = useAlert();

		const [tab, setTab] = useState(0);

		const formMode = useSelector((state) => state.whatsappBusiness.mode);
		const isLoading = useSelector((state) => state.whatsappBusiness.isLoading);
		const isVerified = useSelector((state) => state.whatsappBusiness.isVerified);
		const dispatch = useDispatch();

		const schema = useMemo(
			() =>
				yup.object().shape({
					businessId: yup.string().nullable().required(),
					userAccessToken: yup.string().nullable().required(),
					phoneNumberId: yup.string().nullable().required(),
					connectionName: yup.string().nullable().required(),
					senderName: yup.string().nullable().required(),
					isActive: yup.boolean().nullable(),
					importExistingTemplates: yup.boolean().nullable(),
					isDefault: yup.boolean().nullable()
					// webhookUrl: yup.string().nullable()
				}),
			[]
		);

		const stages = useMemo(
			() => [
				{ name: 'instructions', icon: faBookOpen },
				{ name: 'credentials', icon: faKey },
				{ name: 'settings', icon: faWrench }
			],
			[]
		);

		const formMethods = useForm({
			resolver: yupResolver(schema),
			defaultValues: {
				businessId: null,
				userAccessToken: null,
				phoneNumberId: null,
				connectionName: null,
				isActive: false,
				importExistingTemplates: false,
				isDefault: false
				// webhookUrl: null
			}
		});

		const {
			trigger,
			handleSubmit,
			reset,
			formState: { isDirty }
		} = formMethods;

		const handleCloseModal = () => {
			modal.remove();
		};

		const onSubmit = useCallback(
			async (data) => {
				dispatch(setIsLoading(true));
				const url =
					formMode === 'add' ? WHATSAPP.SAVE_WHATSAPP_INTEGRATION : WHATSAPP.UPDATE_WHATSAPP_INTEGRATION;
				await axiosPrivate
					.post(url, {
						...data
					})
					.then((response) => {
						if (setConnection) {
							setConnection(response.data);
						}
						showSuccess();
						handleCloseModal();
					})
					.catch((error) => {
						showError({
							message: error?.response?.data?.code || error?.response?.data?.message
						});
						AppLogger.log(`Error while saving whatsapp integration: ${JSON.stringify(error)}`);
					});
				dispatch(setIsLoading(false));
			},
			[formMode]
		);

		const setStep = useCallback(
			({ type }) => {
				if (type === 'next') {
					if (tab < 2) {
						setTab(tab + 1);
					}
				} else if (type === 'prev') {
					if (tab > 0) {
						setTab(tab - 1);
					}
				}
			},
			[tab, isVerified]
		);

		const handleTabChange = useCallback(() => {
			if (tab === 1 && !isVerified) {
				trigger(['businessId', 'userAccessToken']);
				showWarning({
					message: 'whatsapp.verifyYourConnection'
				});
			} else {
				setStep({ type: 'next' });
			}
		}, [tab, isVerified]);

		const fetchWhatsappIntegration = useCallback(async (smsConfigID) => {
			axiosPrivate
				.post(WHATSAPP.GET_WHATSAPP_INTEGRATION, { smsConfigID })
				.then((response) => {
					if (response.status === 200) {
						const { data } = response;
						reset({
							businessId: data.whatsappBusinessID ?? null,
							userAccessToken: data.userAccessToken ?? null,
							phoneNumberId: data.businessPhoneNumberId ?? null,
							connectionName: data.connectionName ?? null,
							whatsappUserId: data.whatsappUserId ?? null,
							senderName: data.senderName ?? null,
							smsConfigID: data.smsConfigID ?? null,
							isActive: data.status ?? false,
							importExistingTemplates: data.importExistingTemplates ?? false,
							isDefault: data.isDefault ?? false
							// webhookUrl: data.webhookUrl ?? null
						});
						dispatch(setDefaultConnection(data.isDefault ?? false));
					}
				})
				.catch((error) => {
					AppLogger.log(`Error while fetching whatsapp integration: ${JSON.stringify(error)}`);
					showError();
				});
		}, []);

		useEffect(() => {
			dispatch(setFormMode(mode));
			if (mode === 'edit') {
				fetchWhatsappIntegration(smsConfigID);
				dispatch(setIsVerified(true));
				setTab(2);
			}
			return () => {
				dispatch(setStateToDefault());
			};
		}, []);

		return (
			<Modal fullwidth maxWidth="medium" className="modal-integrationDynamicForm">
				<ModalTitle>
					<S.Header>
						<S.LeftContainer>
							<S.Logo width={'6rem'} height={'6rem'} url={logo} />
							<Typography variant="p1" fontWeight="500">
								whatsappBusiness
							</Typography>
						</S.LeftContainer>
						<S.RightContainer>
							<ProgressWizard stages={stages} setIndex={setTab} index={tab} gutterBottom />
						</S.RightContainer>
					</S.Header>
				</ModalTitle>
				<ModalContent>
					<Form formMethods={formMethods} borderless>
						<ProgressWizardTabs tab={tab} />
					</Form>
				</ModalContent>
				<ModalActions>
					<Button label="cancel" variant="outline" onClick={handleCloseModal} />
					{tab !== 0 && (
						<Button
							label="previous"
							variant="outline"
							icon={faAngleLeft}
							onClick={() => setStep({ type: 'prev' })}
							loading={isLoading}
						/>
					)}
					{tab !== 2 && (
						<Button
							icon={tab !== 2 && faAngleRight}
							label="continue"
							iconPos="right"
							onClick={handleTabChange}
							variant="blue"
							loading={isLoading}
						/>
					)}
					{tab === 2 && (
						<Button
							lordIcon="https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/save-icon.json.gz"
							className="saveButton"
							label="save"
							iconPos="right"
							onClick={handleSubmit(onSubmit, (errors) => {
								AppLogger.log(errors);
							})}
							disabled={!isDirty}
							disabledTooltip={!isDirty && 'formInfo.noChanges'}
							variant="blue"
							loading={isLoading}
						/>
					)}
				</ModalActions>
			</Modal>
		);
	}
);

export default WhatsappBusinessModal;
